<script setup lang="ts">
import {QuestionType, type Question} from '@/ts/types/dto/interventions-dto';
import type {
  QuestionAnswerBody,
  QuestionAnswerMultiChoiceBody,
  QuestionAnswerNumericBody,
  QuestionnaireAnswers,
} from '@/ts/types/dto/session-request-dto';
import {computed} from 'vue';
import ErrorBox from '@/vue/molecules/error-box.vue';
import QuestionNumericComponent from '@/vue/molecules/components/question/question-numeric-component.vue';
import QuestionMultichoiceComponent from '@/vue/molecules/components/question/question-multichoice-component.vue';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';

const props = defineProps<{
  question: Question;
  questionnaireId: UuidDTO;
  questionnaireType?: string;
  displayListWithQuestionInRow?: boolean;
}>();

defineEmits<{
  readyToGoNext: [],
}>();

const model = defineModel<QuestionnaireAnswers | null>();

const value = computed<QuestionAnswerBody | null>({
  get() {
    return model.value?.answers ?? null;
  },
  set(newValue) {
    model.value = {
      answers: newValue!,
      userComponentId: props.question.userComponentId,
      questionId: props.question.contentId,
      questionnaireId: props.questionnaireId,
    };
  },
});

</script>
<template>
  <div class="question-component">
    <div v-if="question.intro" v-safe-html="question.intro" class="intro"/>
    <div class="question-body">
      <question-numeric-component
        v-if="question.numeric && question.type === QuestionType.NUMERIC"
        v-model="value as QuestionAnswerNumericBody|null"
        :question="question"
        :questionnaire-type="questionnaireType"
      />
      <question-multichoice-component
        v-else-if="question.multiChoice && question.type === QuestionType.MULTICHOICE"
        v-model="value as QuestionAnswerMultiChoiceBody|null"
        :display-list-with-question-in-row="displayListWithQuestionInRow"
        :question="question"
        :questionnaire-type="questionnaireType"
        @ready-to-go-next="$emit('readyToGoNext')"
      />
      <error-box v-else/>
    </div>
  </div>
</template>
<style scoped lang="scss">
.question-component {
  display: flex;
  flex-direction: column;
}

.intro {
  @include font-body-sm;
  width: 100%;
  text-align: left;
  color: var(--Neutral-UI-neutral-ui-60);
  margin-bottom: var(--Spacing-spacing-4);
}

// When the container stretches the component (standard modal), the intro
// stays pinned at the top and the question centers in the remaining space.
// In content-sized containers there is no free space and this is a no-op.
.question-body {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
</style>
